import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaApple, FaGooglePlay, FaHeart } from 'react-icons/fa';

const APP_STORE_URL = 'https://apps.apple.com/us/app/pasur-online-play-score/id6476008595';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.vakilventures.pasur';

const FooterContainer = styled.footer`
  background: linear-gradient(180deg, #0a1628 0%, #070d18 100%);
  color: white;
  padding: 4rem 2rem 2rem;
  margin-top: auto;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--color-gold), transparent);
  }

  @media (max-width: 768px) {
    padding: 3rem 1.5rem 1.5rem;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FooterTop = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 4rem;
  margin-bottom: 3rem;
  padding-bottom: 3rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
    gap: 2.5rem;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 2rem;
  }
`;

const BrandSection = styled.div`
  @media (max-width: 600px) {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const FooterLogo = styled(Link)`
  font-family: 'Outfit', sans-serif;
  font-size: 1.75rem;
  font-weight: 700;
  color: white;
  text-decoration: none;
  display: inline-block;
  margin-bottom: 1rem;
  transition: color 0.3s ease;

  span {
    color: var(--color-gold);
  }

  &:hover {
    color: var(--color-gold);
  }
`;

const BrandTagline = styled.p`
  font-family: 'Plus Jakarta Sans', sans-serif;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1rem;
  line-height: 1.7;
  margin: 0 0 1.5rem 0;
  max-width: 300px;

  @media (max-width: 600px) {
    max-width: none;
  }
`;

const StoreButtons = styled.div`
  display: flex;
  gap: 0.75rem;

  @media (max-width: 600px) {
    justify-content: center;
  }
`;

const StoreButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    background: var(--color-gold);
    border-color: var(--color-gold);
    color: var(--color-midnight);
    transform: translateY(-3px);
  }

  svg {
    font-size: 1.25rem;
  }
`;

const FooterSection = styled.div`
  @media (max-width: 600px) {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const FooterTitle = styled.h3`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--color-gold);
  margin-bottom: 1.25rem;
`;

const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FooterLink = styled(Link)`
  font-family: 'Plus Jakarta Sans', sans-serif;
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  font-size: 0.95rem;
  transition: all 0.3s ease;

  &:hover {
    color: white;
    padding-left: 4px;
  }
`;

const ContactLink = styled.a`
  font-family: 'Plus Jakarta Sans', sans-serif;
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  font-size: 0.95rem;
  transition: all 0.3s ease;

  &:hover {
    color: white;
    padding-left: 4px;
  }
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 600px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  font-family: 'Plus Jakarta Sans', sans-serif;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.85rem;
  margin: 0;
`;

const MadeWith = styled.span`
  font-family: 'Plus Jakarta Sans', sans-serif;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;

  svg {
    color: var(--color-coral);
    font-size: 0.9rem;
  }
`;

const Footer: React.FC = () => {
  return (
    <FooterContainer>
      <FooterContent>
        <FooterTop>
          <BrandSection>
            <FooterLogo to="/">
              Pasur<span>.</span>
            </FooterLogo>
            <BrandTagline>
              The classic Iranian card game, beautifully reimagined for your phone.
            </BrandTagline>
            <StoreButtons>
              <StoreButton
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download on App Store"
              >
                <FaApple />
              </StoreButton>
              <StoreButton
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get it on Google Play"
              >
                <FaGooglePlay />
              </StoreButton>
            </StoreButtons>
          </BrandSection>

          <FooterSection>
            <FooterTitle>Navigate</FooterTitle>
            <FooterLinks>
              <FooterLink to="/">Home</FooterLink>
              <FooterLink to="/rules">Game Rules</FooterLink>
              <ContactLink href="mailto:team@pasurscorekeeper.com">Contact</ContactLink>
            </FooterLinks>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Legal</FooterTitle>
            <FooterLinks>
              <FooterLink to="/terms">Terms of Service</FooterLink>
              <FooterLink to="/privacy">Privacy Policy</FooterLink>
            </FooterLinks>
          </FooterSection>
        </FooterTop>

        <FooterBottom>
          <Copyright>
            &copy; {new Date().getFullYear()} Pasur Score Keeper. All rights reserved.
          </Copyright>
          <MadeWith>
            Made with <FaHeart /> in NYC
          </MadeWith>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;
